import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Popper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ThemeProvider,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PropTypes from 'prop-types';
import axios from 'axios';
import Papa from 'papaparse';
import {toast} from 'react-toastify';
import NavBar from '../../../pieces/navbar/navbar';
import myTheme from '../../../styles/myStyles';
import ConfirmModal from '../../../pieces/modals/confirmPopup';
import FormSubmitButtons from '../../../pieces/formSubmitButtons';
import ApiUtils from '../../../utils/apiUtils';
import base_url from '../../../baseUrls';
import AiCallInspectionSearchDialog from '../../../sockets/AiCallInspectionSearchDialog';
import {COLORS} from '../../../styles/styleConstants';
import {CustomInput, FloatingLabel} from '../../../pieces/inputs/customInputComponent';
import * as allConstants from '../../../constants/allConstants';
import RescheduleCallDialog from './attention/rescheduleCallDialog';
import CallDetailsDialog from './attention/callDetailsDialog';

/** Matches the rest of the dashboard so the whole page refreshes on one cadence. */
const REFRESH_INTERVAL_SECONDS = 60;

/** Long enough that a search is not sent on every keystroke. */
const SEARCH_DEBOUNCE_MS = 400;

/** The server caps a page at this, which is also the export limit. */
const EXPORT_LIMIT = 1000;

/**
 * The reasons a call lands here, in the order the server ranks them. Each is a state observed in
 * production, not a category invented for this screen:
 *
 * - unmatched: no job number, so nobody owns the follow-up
 * - unrecognised: a result or status outside the sets the backend handles (`completedd`, `s7`)
 * - refused: sent to the dialer, which would not place it (`Call in progress for this number`)
 * - dropped: hung up, failed, busy, or stuck reporting `in-progress`
 * - noanswer: the dialer reported nobody picked up
 * - rescheduled: the homeowner asked to be called back
 * - phone: wrong number, or the homeowner gave a new one
 */
const REASONS = [
  {key: 'unmatched', label: 'Unmatched', colour: '#F32929'},
  {key: 'unrecognised', label: 'Unrecognised', colour: '#B26A00'},
  {key: 'refused', label: 'Refused', colour: '#00796B'},
  {key: 'dropped', label: 'Dropped', colour: '#7A4AC7'},
  {key: 'noanswer', label: 'No answer', colour: '#4A6FA5'},
  {key: 'rescheduled', label: 'Rescheduled', colour: '#2E7D5B'},
  {key: 'phone', label: 'Phone issue', colour: '#A0527A'},
];

const REASON_COLOUR = REASONS.reduce((acc, r) => ({...acc, [r.key]: r.colour}), {});
const REASON_LABEL = REASONS.reduce((acc, r) => ({...acc, [r.key]: r.label}), {});

/** The filters' own labels name them, so "any" needs no prefix. */
const DIRECTIONS = [
  {value: '', label: 'Any'},
  {value: 'outbound', label: 'Outbound'},
  {value: 'inbound', label: 'Inbound'},
];

const ANSWERED_BY = [
  {value: '', label: 'Any'},
  {value: 'human', label: 'Human'},
  {value: 'voicemail', label: 'Voicemail'},
  {value: 'unknown', label: 'Unknown'},
  {value: 'none', label: 'Not reported'},
];

const FOLLOW_UP_LABEL = {
  pending: 'Queued',
  placed: 'Placed',
  cancelled: 'Cancelled',
};

const EMPTY_FILTERS = {
  companyId: '',
  direction: '',
  answeredBy: '',
  from: '',
  to: '',
  dismissed: false,
};

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString();
};

/** A `YYYY-MM-DD` from a date input as the start of that day in the agent's zone, ISO. */
const dayStartIso = (day, addDays = 0) => {
  if (!day) return undefined;
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d + addDays).toISOString();
};

const homeownerName = (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim();

/** Whether a reschedule makes sense here: there is a job to call about and nothing is queued. */
const canRescheduleRow = (row) => Boolean(row.jobNumber) &&
  !row.onDoNotCall &&
  row.rescheduledCallState !== 'pending';

/** One control in the filter bar, labelled the way the portal's forms are. */
const FilterField = ({id, label, width, children}) => (
  <FormControl variant={'standard'} sx={{width}}>
    <FloatingLabel apiName={id} label={label}/>
    {children}
  </FormControl>
);

FilterField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
};

/** The company list, wider than its field so long company names stay on one line. */
const CompanyPopper = (props) => (
  <Popper {...props} placement={'bottom-start'} style={{...props.style, width: 360}}/>
);

CompanyPopper.propTypes = {
  style: PropTypes.instanceOf(Object),
};

const CELL_SX = {fontSize: '0.85rem', py: 0.75, verticalAlign: 'top'};
const SUB_SX = {color: COLORS.fontPale, fontSize: '0.75rem'};

/**
 * Calls that did not reach a clean, owned outcome.
 *
 * A call being recorded is not the same as a call being seen; this is the queue that closes that
 * gap. Rows carry every reason that applies rather than one, because a call with no job number
 * *and* an unreadable status is both, and showing only the first would bury the second.
 */
export const AiAttentionCalls = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeReason, setActiveReason] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [companies, setCompanies] = useState([]);
  const [exporting, setExporting] = useState(false);

  const [menu, setMenu] = useState(null);
  const [searchCall, setSearchCall] = useState(null);
  const [rescheduleCall, setRescheduleCall] = useState(null);
  const [detailsCall, setDetailsCall] = useState(null);
  const [pendingDnc, setPendingDnc] = useState(null);
  const [pendingCancel, setPendingCancel] = useState(null);
  const [pendingDismiss, setPendingDismiss] = useState(null);
  const [dismissNote, setDismissNote] = useState('');
  const [saving, setSaving] = useState(false);

  const isMountedRef = useRef(false);
  // Responses can land out of order while someone is typing; only the newest request may win.
  const requestSeqRef = useRef(0);

  const queryParams = useMemo(() => {
    const params = {
      page: page + 1,
      limit: rowsPerPage,
      search: search || undefined,
      reason: activeReason === 'all' ? undefined : activeReason,
      companyId: filters.companyId || undefined,
      direction: filters.direction || undefined,
      answeredBy: filters.answeredBy || undefined,
      from: dayStartIso(filters.from),
      to: dayStartIso(filters.to, 1),
      dismissed: filters.dismissed ? 'true' : undefined,
    };
    return params;
  }, [page, rowsPerPage, search, activeReason, filters]);
  const queryParamsRef = useRef(queryParams);
  queryParamsRef.current = queryParams;

  const fetchRows = useCallback(async ({showLoader = false} = {}) => {
    const seq = ++requestSeqRef.current;
    if (showLoader && isMountedRef.current) setLoading(true);
    try {
      const authToken = await ApiUtils.getAccessToken();
      const response = await axios.get(`${base_url.api}ai/calls-needing-attention`, {
        headers: {Authorization: authToken},
        params: queryParamsRef.current,
      });
      if (!isMountedRef.current || seq !== requestSeqRef.current) return;
      const data = response?.data?.data || {};
      setRows(Array.isArray(data.aiCalls) ? data.aiCalls : []);
      setTotal(data.total ?? 0);
      setCounts(data.counts || {});
      setError('');
    } catch (e) {
      if (isMountedRef.current && seq === requestSeqRef.current) {
        setError('Unable to load call exceptions.');
      }
    } finally {
      if (isMountedRef.current && seq === requestSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    const id = setInterval(() => fetchRows(), REFRESH_INTERVAL_SECONDS * 1000);
    return () => {
      isMountedRef.current = false;
      clearInterval(id);
    };
  }, [fetchRows]);

  useEffect(() => {
    fetchRows({showLoader: true});
  }, [queryParams, fetchRows]);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchText.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchText]);

  useEffect(() => {
    (async () => {
      try {
        const authToken = await ApiUtils.getAccessToken();
        const response = await axios.get(`${base_url.api}companies/all-list`, {
          headers: {Authorization: authToken},
        });
        const list = response?.data?.data?.companies;
        if (isMountedRef.current && Array.isArray(list)) {
          setCompanies([...list].sort((a, b) => String(a.name).localeCompare(String(b.name))));
        }
      } catch (e) {
        // The company filter is a convenience; the queue works without it.
      }
    })();
  }, []);

  const setFilter = (key, value) => {
    setFilters((prev) => ({...prev, [key]: value}));
    setPage(0);
  };

  const filtersActive = Boolean(search) ||
    Object.keys(EMPTY_FILTERS).some((k) => filters[k] !== EMPTY_FILTERS[k]);

  const clearFilters = () => {
    setSearchText('');
    setSearch('');
    setFilters(EMPTY_FILTERS);
    setPage(0);
  };

  const post = async (path, body) => {
    const authToken = await ApiUtils.getAccessToken();
    const response = await axios.post(`${base_url.api}ai/${path}`, body,
      {headers: {Authorization: authToken}});
    return response?.data?.data || {};
  };

  const runAction = async (action, successMessage, failMessage) => {
    setSaving(true);
    try {
      const data = await action();
      toast(typeof successMessage === 'function' ? successMessage(data) : successMessage,
        {type: 'success'});
      fetchRows();
      return true;
    } catch (err) {
      toast(err?.response?.data?.message ?? failMessage, {type: 'error'});
      return false;
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  };

  const submitDnc = async () => {
    const target = pendingDnc;
    const ok = await runAction(
      () => post('add-call-to-do-not-call', {aiCallId: target.id}),
      (data) => `${data.phoneNumber || target.phone} ${data.added === false
        ? 'was already on' : 'added to'} the do-not-call list.${data.cancelledQueuedCalls
        ? ` ${data.cancelledQueuedCalls} queued call(s) cancelled.` : ''}`,
      'Could not add this number to do-not-call.',
    );
    if (ok) setPendingDnc(null);
  };

  const submitCancel = async () => {
    const ok = await runAction(
      () => post('cancel-follow-up-call', {aiCallId: pendingCancel.id}),
      'Queued call cancelled. The inspection moves to Needs Verify.',
      'Could not cancel the queued call.',
    );
    if (ok) setPendingCancel(null);
  };

  const submitDismiss = async () => {
    const ok = await runAction(
      () => post('dismiss-attention-call', {aiCallId: pendingDismiss.id, note: dismissNote}),
      'Call dismissed from Call Exceptions.',
      'Could not dismiss this call.',
    );
    if (ok) setPendingDismiss(null);
  };

  const restore = (row) => runAction(
    () => post('dismiss-attention-call', {aiCallId: row.id, dismissed: false}),
    'Call is back in Call Exceptions.',
    'Could not restore this call.',
  );

  const exportCsv = async () => {
    setExporting(true);
    try {
      const authToken = await ApiUtils.getAccessToken();
      const response = await axios.get(`${base_url.api}ai/calls-needing-attention`, {
        headers: {Authorization: authToken},
        params: {...queryParamsRef.current, page: 1, limit: EXPORT_LIMIT},
      });
      const list = response?.data?.data?.aiCalls || [];
      const csv = Papa.unparse(list.map((row) => ({
        'Reasons': (row.attentionReasons || []).map((r) => REASON_LABEL[r] || r).join('; '),
        'Job #': row.jobNumber,
        'Company': row.companyName,
        'Homeowner': homeownerName(row),
        'Phone': row.phone,
        'Address': row.address,
        'Direction': row.direction,
        'Result': row.result,
        'Call status': row.callStatus,
        'Dialer error': row.dialerError,
        'Answered by': row.answeredBy,
        'Attempts': row.attempt,
        'No-answers in a row': row.noAnswerStreak,
        'Received': formatDate(row.createdAt),
        'Rescheduled for (homeowner time)': row.rescheduledForLocal,
        'Follow-up state': row.rescheduledCallState,
        'Do not call': row.onDoNotCall ? 'Yes' : 'No',
        'Dismissed': formatDate(row.dismissedAt),
        'Dismissed by': row.dismissedBy,
        'Dismiss note': row.dismissedNote,
        'Summary': row.summary,
      })));
      const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `call-exceptions-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      if (total > list.length) {
        toast(`Exported the newest ${list.length} of ${total} calls.`, {type: 'info'});
      }
    } catch (err) {
      toast('Could not export the calls.', {type: 'error'});
    } finally {
      setExporting(false);
    }
  };

  const openMenu = (event, row) => setMenu({anchor: event.currentTarget, row});
  const closeMenu = () => setMenu(null);
  const fromMenu = (fn) => () => {
    const row = menu?.row;
    closeMenu();
    if (row) fn(row);
  };

  const renderAttempts = (row) => {
    if (!row.jobNumber) return '-';
    const limit = row.maxNoAnswerAttempts || 0;
    const reachedLimit = limit > 0 && row.noAnswerStreak >= limit;
    return (
      <>
        {row.attempt || '-'}
        {row.noAnswerStreak > 0 && (
          <Typography sx={{...SUB_SX, ...(reachedLimit && {color: COLORS.globalRed})}}>
            {limit > 0
              ? `${row.noAnswerStreak} of ${limit} unanswered`
              : `${row.noAnswerStreak} unanswered in a row`}
          </Typography>
        )}
        {reachedLimit && !row.rescheduledFor && (
          <Typography sx={{...SUB_SX, color: COLORS.globalRed}}>
            Limit reached — not auto-rescheduled
          </Typography>
        )}
      </>
    );
  };

  const renderFollowUp = (row) => {
    if (!row.rescheduledFor) {
      return '-';
    }
    const state = row.rescheduledCallState;
    const phoneChanged = row.rescheduledPhone &&
      String(row.rescheduledPhone).replace(/\D/g, '').slice(-10) !==
      String(row.phone || '').replace(/\D/g, '').slice(-10);
    return (
      <>
        {row.rescheduledForLocal || formatDate(row.rescheduledFor)}
        {state && (
          <Typography sx={{...SUB_SX, ...(state === 'cancelled' && {color: COLORS.globalRed})}}>
            {FOLLOW_UP_LABEL[state] || state}
          </Typography>
        )}
        {phoneChanged && (
          <Typography sx={SUB_SX}>{`To ${row.rescheduledPhone}`}</Typography>
        )}
      </>
    );
  };

  const renderRow = (row) => {
    const reasons = row.attentionReasons || [];
    const canLink = reasons.includes('unmatched');
    const jobHref = row.inspection_job_id
      ? `${allConstants.PATH.JOBS}?job=${row.inspection_job_id}`
      : null;
    return (
      <TableRow key={row.id} hover sx={row.dismissedAt ? {opacity: 0.7} : undefined}>
        <TableCell sx={CELL_SX}>
          <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
            {reasons.map((reason) => (
              <Chip
                key={reason}
                label={REASON_LABEL[reason] || reason}
                size={'small'}
                sx={{
                  backgroundColor: REASON_COLOUR[reason] || COLORS.fontPale,
                  color: '#fff',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  height: '20px',
                }}
              />
            ))}
            {row.onDoNotCall && (
              <Chip
                label={'Do not call'}
                size={'small'}
                variant={'outlined'}
                sx={{fontSize: '0.7rem', fontWeight: 600, height: '20px'}}
              />
            )}
          </Box>
          <Typography sx={{...SUB_SX, mt: 0.5}}>{row.direction}</Typography>
        </TableCell>
        <TableCell sx={CELL_SX}>
          {jobHref ? (
            <Link href={jobHref} target={'_blank'} rel={'noopener'} underline={'hover'}>
              {row.jobNumber}
            </Link>
          ) : (row.jobNumber || '-')}
          <Typography sx={SUB_SX}>{row.companyName || ''}</Typography>
        </TableCell>
        <TableCell sx={CELL_SX}>
          {homeownerName(row) || '-'}
          <Typography sx={SUB_SX}>{row.phone || 'no phone'}</Typography>
        </TableCell>
        <TableCell sx={{...CELL_SX, maxWidth: 240, wordBreak: 'break-word'}}>
          {row.address || '-'}
        </TableCell>
        <TableCell sx={CELL_SX}>
          {row.result || '-'}
          <Typography sx={SUB_SX}>
            {[row.callStatus, row.answeredBy].filter(Boolean).join(' · ')}
          </Typography>
          {row.dialerError && (
            <Typography sx={SUB_SX}>{`Dialer: ${row.dialerError}`}</Typography>
          )}
        </TableCell>
        <TableCell sx={CELL_SX}>{renderAttempts(row)}</TableCell>
        <TableCell sx={CELL_SX}>{formatDate(row.createdAt)}</TableCell>
        <TableCell sx={CELL_SX}>{renderFollowUp(row)}</TableCell>
        <TableCell sx={{...CELL_SX, whiteSpace: 'nowrap'}}>
          <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 0.5}}>
            {canLink && (
              <Button
                size={'small'}
                variant={'outlined'}
                onClick={() => setSearchCall({
                  aiCallId: row.id,
                  address: row.address,
                  answer: row.reason,
                  direction: row.direction,
                  homeOwnerName: homeownerName(row),
                  homeOwnerPhone: row.phone || '',
                })}
                sx={{fontSize: '0.75rem', fontWeight: 600}}
              >
                Find inspection
              </Button>
            )}
            {!canLink && canRescheduleRow(row) && !row.dismissedAt && (
              <Button
                size={'small'}
                variant={'outlined'}
                onClick={() => setRescheduleCall(row)}
                sx={{fontSize: '0.75rem', fontWeight: 600}}
              >
                Reschedule
              </Button>
            )}
            <IconButton size={'small'} onClick={(e) => openMenu(e, row)} aria-label={'More actions'}>
              <MoreVertIcon fontSize={'small'}/>
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const menuRow = menu?.row;

  return (
    <>
      <NavBar />
      <ThemeProvider theme={myTheme}>
        {/* A column filling the dashboard panel: the table takes what the bars above leave and
            scrolls inside itself, so its header row and the paging stay on screen. */}
        <Box sx={{
          px: 2,
          py: 1.5,
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box sx={{alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1}}>
            <Typography sx={{fontSize: '1.05rem', fontWeight: 700, mr: 1}}>
              {filters.dismissed ? 'Dismissed' : 'Call Exceptions'}
            </Typography>
            {[{key: 'all', label: 'All'}, ...REASONS].map((r) => (
              <Chip
                key={r.key}
                label={`${r.label} (${counts[r.key] ?? 0})`}
                onClick={() => {
                  setActiveReason(r.key);
                  setPage(0);
                }}
                variant={activeReason === r.key ? 'filled' : 'outlined'}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  ...(activeReason === r.key && {
                    backgroundColor: r.colour || COLORS.globalBlue,
                    color: '#fff',
                  }),
                }}
              />
            ))}
            <Box sx={{flexGrow: 1}} />
            <Button variant={'outlined'} size={'small'} onClick={exportCsv} disabled={exporting}>
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
            <Button variant={'outlined'} size={'small'} onClick={() => fetchRows()}>
              Refresh
            </Button>
          </Box>

          {/* The portal's own fields (label on the border, `CustomInput` box) rather than MUI
              TextFields: the theme draws a TextField's label over its value, and mixing the two
              left every control a different height. */}
          <Box sx={{alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5}}>
            <FilterField id={'exceptions-search'} label={'Search'} width={260}>
              <CustomInput
                id={'exceptions-search'}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={'Name, phone, address, job #'}
                fullWidth
              />
            </FilterField>
            <FilterField id={'exceptions-company'} label={'Company'} width={220}>
              <Autocomplete
                id={'exceptions-company'}
                PopperComponent={CompanyPopper}
                options={companies}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={companies.find((c) => c.id === filters.companyId) || null}
                onChange={(e, value) => setFilter('companyId', value?.id || '')}
                renderInput={(params) => (
                  <CustomInput
                    ref={params.InputProps.ref}
                    inputProps={params.inputProps}
                    placeholder={'Any'}
                    fullWidth
                  />
                )}
              />
            </FilterField>
            <FilterField id={'exceptions-direction'} label={'Direction'} width={140}>
              <Select
                value={filters.direction}
                onChange={(e) => setFilter('direction', e.target.value)}
                displayEmpty
                input={<CustomInput id={'exceptions-direction'}/>}
              >
                {DIRECTIONS.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
              </Select>
            </FilterField>
            <FilterField id={'exceptions-answered-by'} label={'Answered by'} width={150}>
              <Select
                value={filters.answeredBy}
                onChange={(e) => setFilter('answeredBy', e.target.value)}
                displayEmpty
                input={<CustomInput id={'exceptions-answered-by'}/>}
              >
                {ANSWERED_BY.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
              </Select>
            </FilterField>
            <FilterField id={'exceptions-from'} label={'Received from'} width={150}>
              <CustomInput
                id={'exceptions-from'}
                type={'date'}
                value={filters.from}
                onChange={(e) => setFilter('from', e.target.value)}
                fullWidth
              />
            </FilterField>
            <FilterField id={'exceptions-to'} label={'Received to'} width={150}>
              <CustomInput
                id={'exceptions-to'}
                type={'date'}
                value={filters.to}
                onChange={(e) => setFilter('to', e.target.value)}
                fullWidth
              />
            </FilterField>
            <FormControlLabel
              control={
                <Switch
                  size={'small'}
                  checked={filters.dismissed}
                  onChange={(e) => setFilter('dismissed', e.target.checked)}
                />
              }
              label={<Typography sx={{fontSize: '0.85rem'}}>Show dismissed</Typography>}
            />
            {filtersActive && (
              <Button size={'small'} onClick={clearFilters}>Clear filters</Button>
            )}
          </Box>

          {loading && <Typography>Loading...</Typography>}
          {!loading && error && <Typography color={'error'}>{error}</Typography>}
          {!loading && !error && rows.length === 0 && (
            <Typography>
              {filtersActive || activeReason !== 'all'
                ? 'No calls match these filters.'
                : 'No call exceptions.'}
            </Typography>
          )}

          {!loading && !error && rows.length > 0 && (
            <>
              <TableContainer component={Box} sx={{flex: '1 1 0', minHeight: 0, overflow: 'auto'}}>
                <Table size={'small'} stickyHeader sx={{minWidth: 1250}}>
                  <TableHead>
                    <TableRow>
                      {['Reason', 'Job # / company', 'Homeowner', 'Address', 'Result', 'Attempts',
                        'Received', 'Rescheduled for', ''].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            backgroundColor: '#fff',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>{rows.map(renderRow)}</TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component={'div'}
                count={total}
                page={page}
                onPageChange={(e, next) => setPage(next)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={[25, 50, 100]}
                // Clear of the accessibility button that floats over the bottom-right corner. On the
                // toolbar: MUI zeroes the root's padding when it is its parent's last child.
                sx={{flexShrink: 0, '& .MuiTablePagination-toolbar': {pr: 6}}}
              />
            </>
          )}

          <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={closeMenu}>
            <MenuItem onClick={fromMenu(setDetailsCall)}>View summary & transcript</MenuItem>
            {menuRow?.inspection_job_id && (
              <MenuItem
                component={'a'}
                href={`${allConstants.PATH.JOBS}?job=${menuRow.inspection_job_id}`}
                target={'_blank'}
                rel={'noopener'}
                onClick={closeMenu}
              >
                Open job
              </MenuItem>
            )}
            {menuRow && canRescheduleRow(menuRow) && (
              <MenuItem onClick={fromMenu(setRescheduleCall)}>
                Reschedule / call now / change phone...
              </MenuItem>
            )}
            {menuRow?.rescheduledCallState === 'pending' && (
              <MenuItem onClick={fromMenu(setPendingCancel)}>Cancel queued call</MenuItem>
            )}
            {menuRow?.phone && !menuRow.onDoNotCall && (
              <MenuItem
                onClick={fromMenu((row) => setPendingDnc({
                  id: row.id,
                  name: homeownerName(row) || 'this homeowner',
                  phone: row.phone,
                }))}
                sx={{color: COLORS.globalRed}}
              >
                Add to do not call
              </MenuItem>
            )}
            {menuRow && !menuRow.dismissedAt && (
              <MenuItem onClick={fromMenu((row) => {
                setDismissNote('');
                setPendingDismiss(row);
              })}>
                Dismiss
              </MenuItem>
            )}
            {menuRow?.dismissedAt && (
              <MenuItem onClick={fromMenu(restore)}>Restore to Call Exceptions</MenuItem>
            )}
          </Menu>

          {pendingDnc && (
            <ConfirmModal open={true} handleClose={() => !saving && setPendingDnc(null)}>
              <Box sx={{'& .MuiTypography-root': {fontSize: '1rem'}}}>
                <Typography variant={'body1'} mb={1}>
                  {'Add '}
                  <Typography component={'span'} fontWeight={600} color={COLORS.globalBlue}>
                    {pendingDnc.phone}
                  </Typography>
                  {` (${pendingDnc.name}) to the do-not-call list?`}
                </Typography>
                <Typography variant={'body1'} mb={3} color={COLORS.fontPale}>
                  The AI will stop calling this number: any queued call for it is cancelled and
                  AI calls are switched off on the inspection.
                </Typography>
                <FormSubmitButtons
                  cancelText={'No'}
                  submitText={'Yes'}
                  cancelAction={() => setPendingDnc(null)}
                  submitAction={submitDnc}
                  disableSubmit={saving}
                  disableCancel={saving}
                />
              </Box>
            </ConfirmModal>
          )}

          {pendingCancel && (
            <ConfirmModal open={true} handleClose={() => !saving && setPendingCancel(null)}>
              <Box sx={{'& .MuiTypography-root': {fontSize: '1rem'}}}>
                <Typography variant={'body1'} mb={1}>
                  {`Cancel the call queued for ${pendingCancel.rescheduledForLocal ||
                    formatDate(pendingCancel.rescheduledFor)}?`}
                </Typography>
                <Typography variant={'body1'} mb={3} color={COLORS.fontPale}>
                  Nothing else will be queued for this homeowner, and the inspection moves to
                  Needs Verify so someone picks it up.
                </Typography>
                <FormSubmitButtons
                  cancelText={'No'}
                  submitText={'Yes, cancel it'}
                  cancelAction={() => setPendingCancel(null)}
                  submitAction={submitCancel}
                  disableSubmit={saving}
                  disableCancel={saving}
                />
              </Box>
            </ConfirmModal>
          )}
        </Box>
      </ThemeProvider>

      <Dialog
        open={Boolean(pendingDismiss)}
        onClose={() => !saving && setPendingDismiss(null)}
        maxWidth={'sm'}
        fullWidth
      >
        <DialogTitle sx={{fontWeight: 700}}>Dismiss from Call Exceptions</DialogTitle>
        <DialogContent>
          <Typography sx={{color: COLORS.fontPale, fontSize: '0.9rem', mb: 2}}>
            The call leaves this queue; nothing about it changes. A call already queued still goes
            out — cancel it first if it should not.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={'Note (optional)'}
            value={dismissNote}
            onChange={(e) => setDismissNote(e.target.value)}
            inputProps={{maxLength: 500}}
          />
        </DialogContent>
        <DialogActions sx={{px: 3, pb: 2}}>
          <Button onClick={() => setPendingDismiss(null)} disabled={saving}>Cancel</Button>
          <Button variant={'contained'} onClick={submitDismiss} disabled={saving}>
            {saving ? 'Dismissing...' : 'Dismiss'}
          </Button>
        </DialogActions>
      </Dialog>

      <RescheduleCallDialog
        open={Boolean(rescheduleCall)}
        call={rescheduleCall}
        onClose={(changed) => {
          setRescheduleCall(null);
          if (changed) fetchRows();
        }}
      />

      <CallDetailsDialog
        open={Boolean(detailsCall)}
        call={detailsCall}
        onClose={() => setDetailsCall(null)}
      />

      <AiCallInspectionSearchDialog
        open={Boolean(searchCall)}
        call={searchCall}
        onClose={() => {
          setSearchCall(null);
          fetchRows();
        }}
      />
    </>
  );
};

export default AiAttentionCalls;
